import React, { ReactChild } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import BSModal from 'react-bootstrap/Modal'
import { _ } from '../../utils/localization'
import openChatModal from '../chat/openChatModal'
import colors from '../../constants/colors'
import Icon from '../../components/Icon'
import GeneralInfoWithThumbnail from '../../components/GeneralInfoWithThumbnail'
import getMugshotSrc from '../../utils/getMugshotSrc'
import Bookmark from '../goodiebag/Bookmark'
import { GoodiebagType } from '../goodiebag/types'
import { VirtualAttendee } from '../../types/swagger'
import formatJobInfo from '../../utils/formatJobInfo'
import useSlug from '../../hooks/useSlug'
import { currentUserSelector } from '../auth/authSlice'

export const S = {
    BusinessCardBody: styled.p`
        padding: 8px 0;
        margin-top: 8px;
        white-space: pre-line;
    `,
    BusinessCardFooterHeader: styled.p`
        margin: 16px -16px;
        padding: 4px 16px;
        background-color: #386080;
        color: white;
        font-weight: bold;
    `,
    BusinessCardFooter: styled.div`
        padding: 16px 0;
        display: flex;
        justify-content: space-evenly;
    `,
    Connect: styled.div`
        padding: 16px 0;
        display: flex;
        justify-content: space-between;
        width: 60px;
        height: 30px;
        line-height: 30px;
        text-align: center;
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
        background-color: ${colors.primary};
        color: white;
        cursor: pointer;
        transform: translateY(-0.5px);
    `,
    // eslint-disable-next-line react/display-name
    Icon: (props: any) => <Icon size={36} {...props} />,
}

export const getIconColor = (v: string | boolean) => (v ? colors.primary : colors.gray5)

export const Anchor = ({ link, children }: { link: string, children: ReactChild }) => (link
    ? (
        <a href={link} target="__blank">
            {children}
        </a>
    )
    : (
        <>
            {children}
        </>
    ))

const BusinessCard = ({ user }: { user: VirtualAttendee }) => {
    const eventSlug = useSlug('eventSlug')
    const dispatch = useDispatch()
    const handleOpenChat = () => dispatch(openChatModal(eventSlug, user.id))
    const description = formatJobInfo(user.jobtitle, user.company)
    const currentUser = useSelector(currentUserSelector())
    const isOwnCard = user.id === currentUser.id
    const isPublic = user.is_public || isOwnCard

    return (
        <div>
            <BSModal.Header closeButton style={{ paddingTop: 0, paddingLeft: 0 }}>
                <GeneralInfoWithThumbnail
                    image={getMugshotSrc(user)}
                    name={`${user.first_name} ${user.last_name}`}
                    description={description}
                    noBorder
                    badge={user?.is_online || isOwnCard ? 'success' : 'danger'}
                    style={{ flex: 0 }}
                >
                    <Bookmark item={{ id: user.id, type: GoodiebagType.Attendee }} />
                </GeneralInfoWithThumbnail>
            </BSModal.Header>

            <S.BusinessCardBody>
                {isPublic && user.about
                    ? user.about
                    : _('profile.card.no_about_for_user')}
            </S.BusinessCardBody>

            <S.BusinessCardFooterHeader>
                {_('profile.card.connect_through')}
            </S.BusinessCardFooterHeader>

            <S.BusinessCardFooter>
                {isPublic && (
                    <>
                        <Anchor link={user.linkedin}>
                            <S.Icon
                                icon="linkedIn"
                                color={getIconColor(user.linkedin)}
                            />
                        </Anchor>

                        <Anchor link={user.facebook}>
                            <S.Icon
                                icon="facebook"
                                color={getIconColor(user.facebook)}
                            />
                        </Anchor>

                        <Anchor link={user.twitter}>
                            <S.Icon
                                icon="twitter"
                                color={getIconColor(user.twitter)}
                            />
                        </Anchor>

                        <Anchor link={user.instagram}>
                            <S.Icon
                                icon="instagram"
                                color={getIconColor(user.instagram)}
                            />
                        </Anchor>
                    </>
                )}
                <S.Icon
                    icon="message"
                    onClick={isOwnCard ? null : handleOpenChat}
                    color={getIconColor(!isOwnCard)}
                />
            </S.BusinessCardFooter>
        </div>
    )
}

export default BusinessCard
